import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type FirestoreDataConverter,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  assertMeeting,
  meetingSchema,
  meetingWriteSchema,
  nowIsoString,
  type Meeting,
  type MeetingWrite,
} from '@penticton-dev-events/shared';

const meetingConverter: FirestoreDataConverter<Meeting> = {
  toFirestore(meeting) {
    const {
      id: ignoredId,
      location,
      onlineUrl,
      capacity,
      posterUrl,
      posterAlt,
      posterStoragePath,
      agenda,
      coHosts,
      tags,
      ...rest
    } = meeting;
    void ignoredId;

    // Only include fields that are not undefined
    const result: Record<string, unknown> = { ...rest };

    if (location !== undefined) {
      result.location = location;
    }
    if (onlineUrl !== undefined) {
      result.onlineUrl = onlineUrl;
    }
    if (capacity !== undefined) {
      result.capacity = capacity;
    }
    if (posterUrl !== undefined) {
      result.posterUrl = posterUrl;
    }
    if (posterAlt !== undefined) {
      result.posterAlt = posterAlt;
    }
    if (posterStoragePath !== undefined) {
      result.posterStoragePath = posterStoragePath;
    }
    if (agenda !== undefined) {
      result.agenda = agenda;
    }
    if (coHosts !== undefined) {
      result.coHosts = coHosts;
    }
    if (tags !== undefined) {
      result.tags = tags;
    }

    return result;
  },
  fromFirestore(snapshot, options) {
    const payload = snapshot.data(options);
    return assertMeeting({ id: snapshot.id, ...payload });
  },
};

const meetingsCollection = (firestore: Firestore) =>
  collection(firestore, 'meetings').withConverter(meetingConverter);

export const subscribeToMeetingsByGroup = (
  firestore: Firestore,
  groupId: string,
  handleSnapshot: (meetings: Meeting[]) => void,
  handleError: (error: Error) => void,
): Unsubscribe => {
  const meetingsQuery = query(
    meetingsCollection(firestore),
    where('groupId', '==', groupId),
    orderBy('startsAt', 'asc'),
  );

  return onSnapshot(
    meetingsQuery,
    (snapshot: QuerySnapshot<Meeting>) => {
      const meetings = snapshot.docs.map(docSnapshot => {
        const data = docSnapshot.data();
        return data;
      });
      handleSnapshot(meetings);
    },
    error => {
      handleError(error);
    },
  );
};

export const createMeeting = async (
  firestore: Firestore,
  payload: MeetingWrite,
): Promise<Meeting> => {
  const parsed = meetingWriteSchema.parse(payload);
  const meetingsRef = meetingsCollection(firestore);
  const meetingRef = doc(meetingsRef);
  const meeting = meetingSchema.parse({
    ...parsed,
    id: meetingRef.id,
    attendeeCount: parsed.attendeeCount ?? 0,
    createdAt: parsed.createdAt ?? nowIsoString(),
  });

  await setDoc(meetingRef, meetingConverter.toFirestore(meeting));

  return meeting;
};

export const updateMeetingStatus = async (
  firestore: Firestore,
  meetingId: string,
  status: Meeting['status'],
): Promise<void> => {
  const meetingRef = doc(meetingsCollection(firestore), meetingId);
  await updateDoc(meetingRef, { status });
};

/**
 * Update meeting with full meeting data
 */
export const updateMeeting = async (
  firestore: Firestore,
  meetingId: string,
  updates: Partial<MeetingWrite>,
): Promise<void> => {
  const meetingRef = doc(meetingsCollection(firestore), meetingId);

  // Validate updates follow schema
  const parsedUpdates = meetingWriteSchema.partial().parse(updates);

  // Remove undefined fields for cleaner Firestore updates
  const updateData: Record<string, any> = {};
  Object.entries(parsedUpdates).forEach(([key, value]) => {
    if (value !== undefined) {
      updateData[key] = value;
    }
  });

  await updateDoc(meetingRef, updateData);
};

/**
 * Update meeting poster information
 */
export const updateMeetingPoster = async (
  firestore: Firestore,
  meetingId: string,
  posterData: {
    posterUrl: string;
    posterStoragePath: string;
    posterAlt?: string;
  },
): Promise<void> => {
  const meetingRef = doc(meetingsCollection(firestore), meetingId);
  const updateData: Partial<Meeting> = {
    posterUrl: posterData.posterUrl,
    posterStoragePath: posterData.posterStoragePath,
  };

  if (posterData.posterAlt) {
    updateData.posterAlt = posterData.posterAlt;
  }

  await updateDoc(meetingRef, updateData);
};

/**
 * Remove meeting poster
 */
export const removeMeetingPoster = async (
  firestore: Firestore,
  meetingId: string,
): Promise<void> => {
  const meetingRef = doc(meetingsCollection(firestore), meetingId);
  await updateDoc(meetingRef, {
    posterUrl: null,
    posterStoragePath: null,
    posterAlt: null,
  });
};

/**
 * Atomic update of meeting with poster data - useful for poster upload workflows
 */
export const updateMeetingWithPoster = async (
  firestore: Firestore,
  meetingId: string,
  meetingUpdate: Partial<MeetingWrite>,
  posterData?: {
    posterUrl: string;
    posterStoragePath: string;
    posterAlt?: string;
  },
): Promise<void> => {
  const batch = writeBatch(firestore);
  const meetingRef = doc(meetingsCollection(firestore), meetingId);

  const updateData: Record<string, any> = { ...meetingUpdate };

  if (posterData) {
    updateData.posterUrl = posterData.posterUrl;
    updateData.posterStoragePath = posterData.posterStoragePath;
    if (posterData.posterAlt) {
      updateData.posterAlt = posterData.posterAlt;
    }
  }

  batch.update(meetingRef, updateData);
  await batch.commit();
};

/**
 * Delete a meeting
 * Only admins and the meeting organizer should be able to delete meetings
 */
export const deleteMeeting = async (
  firestore: Firestore,
  meetingId: string,
): Promise<void> => {
  const meetingRef = doc(meetingsCollection(firestore), meetingId);
  await deleteDoc(meetingRef);
};
